import React, { useState, useEffect, useMemo } from "react";
import Table from "../../applicationUi/components/Table";
import SearchDropdown from "../../applicationUi/components/SearchDropdown";
import QuickAddClinicianModal from "../../modals/QuickAddClinicianModal";
import ClinicianDetailsModal from "./ClinicianDetailsModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinicians } from "../../store/thunks/cliniciansThunk";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import { fetchPayrollCohorts } from "../../store/thunks/payrollCohortThunk";
import { logInfo, logError } from "../../utils/logger";
import { Listbox, Transition, Dialog } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon, CalendarDaysIcon, BuildingOffice2Icon } from "@heroicons/react/20/solid";

const CliniciansPage = () => {
  const dispatch = useDispatch();
  const clinicians = useSelector((state) => state.clinicians.clinicians || []);
  const clinics = useSelector((state) => state.clinics.clinics || []);
  const payrollCohorts = useSelector((state) => state.payrollCohorts?.payrollCohorts || []);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [isQuickAddClinicianModalOpen, setIsQuickAddClinicianModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  
  // New state for search and filtering
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedSearchResult, setSelectedSearchResult] = useState(null);
  const [selectedPayrollCohort, setSelectedPayrollCohort] = useState(null);

  // State for clinic availability modal
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [selectedClinicianForAvailability, setSelectedClinicianForAvailability] = useState(null);

  useEffect(() => {
    // Fetch clinicians, clinics, and payroll cohorts on component mount
    Promise.all([
      dispatch(fetchClinicians()).unwrap(),
      dispatch(fetchClinics()).unwrap(),
      dispatch(fetchPayrollCohorts()).unwrap()
    ])
      .then(() => {
        logInfo("Clinicians, clinics, and payroll cohorts fetched successfully", {
          componentName: "CliniciansPage",
          action: "fetchData",
        });
      })
      .catch((error) => {
        logError("Failed to fetch data", {
          componentName: "CliniciansPage",
          action: "fetchData",
          error: error.message,
        });
      });
  }, [dispatch]);

  useEffect(() => {
    console.log("selectedClinician updated:", selectedClinician);
  }, [selectedClinician]);

  const handleRefresh = () => {
    Promise.all([
      dispatch(fetchClinicians()).unwrap(),
      dispatch(fetchClinics()).unwrap(),
      dispatch(fetchPayrollCohorts()).unwrap()
    ])
      .then(() => {
        logInfo("Data refreshed successfully", {
          componentName: "CliniciansPage",
          action: "refreshData",
        });
      })
      .catch((error) => {
        logError("Failed to refresh data", {
          componentName: "CliniciansPage",
          action: "refreshData",
          error: error.message,
        });
      });
  };

  // New handlers for search and filtering
  const handleSearchSelection = (clinician) => {
    setSelectedSearchResult(clinician);
    logInfo("Clinician selected from search", {
      componentName: "CliniciansPage",
      action: "searchSelection",
      clinicianId: clinician._id,
    });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSelectedSearchResult(null);
  };

  const handleClinicFilterChange = (clinic) => {
    setSelectedClinic(clinic);
    logInfo("Clinic filter changed", {
      componentName: "CliniciansPage",
      action: "clinicFilter",
      clinicId: clinic?._id,
    });
  };

  const handleClearFilters = () => {
    setSelectedClinic(null);
    setSearchQuery("");
    setSelectedSearchResult(null);
    setSelectedPayrollCohort(null);
  };

  const handleCohortFilterChange = (cohort) => {
    setSelectedPayrollCohort(cohort);
    // Reset clinic selection when cohort changes
    setSelectedClinic(null);
    logInfo("Cohort filter changed", {
      componentName: "CliniciansPage",
      action: "cohortFilter",
      cohortId: cohort?._id,
    });
  };

  const handleCloseQuickAddClinicianModal = () => {
    setIsQuickAddClinicianModalOpen(false);
    logInfo("Quick Add Clinician modal closed", {
      componentName: "CliniciansPage",
      action: "closeQuickAddClinicianModal",
    });
  };

  const handleAddClinician = () => {
    setIsQuickAddClinicianModalOpen(true);
    logInfo("Quick Add Clinician modal opened", {
      componentName: "CliniciansPage",
      action: "openQuickAddClinicianModal",
    });
  };

  const handleClinicianClick = (clinician) => {
    console.log("Clinician row clicked:", clinician);
    setSelectedClinician(clinician);
  };

  // Helper function to format current clinic availability for display
  const getCurrentAvailabilityDisplay = (clinician) => {
    const currentClinics = getCurrentClinics(clinician);
    
    if (currentClinics.length === 0) {
      return 'Not currently available';
    }

    if (currentClinics.length === 1) {
      return currentClinics[0];
    }

    return `${currentClinics.length} clinics`;
  };

  // Handler for showing clinic availability modal
  const handleShowAvailability = (clinician, event) => {
    event.stopPropagation(); // Prevent row click
    setSelectedClinicianForAvailability(clinician);
    setAvailabilityModalOpen(true);
    logInfo("Showing clinic availability", {
      componentName: "CliniciansPage",
      action: "showAvailability",
      clinicianId: clinician._id,
    });
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const getSortedClinicians = (clinicianList) => {
    if (!sortConfig.key) return clinicianList;

    return [...clinicianList].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  // Helper function to get all current clinics for a clinician (they can work at multiple simultaneously)
  const getCurrentClinicIds = (clinician) => {
    if (!clinician.availability || clinician.availability.length === 0) {
      return [];
    }

    const now = new Date();
    const currentAvailabilities = clinician.availability.filter(avail => {
      const startDate = avail.startDate ? new Date(avail.startDate) : null;
      const endDate = avail.endDate ? new Date(avail.endDate) : null;
      
      return (!startDate || startDate <= now) && (!endDate || endDate >= now);
    });

    return currentAvailabilities.map(avail => avail.clinic).filter(Boolean);
  };

  // Helper function to get current clinics with names for display
  const getCurrentClinics = (clinician) => {
    const currentClinicIds = getCurrentClinicIds(clinician);
    return currentClinicIds.map(clinicId => {
      const clinic = clinics.find(c => c._id === clinicId);
      return clinic?.name || 'Unknown Clinic';
    }).filter(Boolean);
  };

  // Enhanced filtering logic based on current availability
  const filteredClinicians = useMemo(() => {
    let filtered = clinicians;

    // Filter by cohort first (if selected)
    if (selectedPayrollCohort) {
      // Get clinics in the selected cohort
      const clinicsInCohort = clinics.filter(c => c.payrollCohort?._id === selectedPayrollCohort._id);
      const clinicIdsInCohort = clinicsInCohort.map(c => c._id);
      
      filtered = filtered.filter((clinician) => {
        const currentClinicIds = getCurrentClinicIds(clinician);
        return currentClinicIds.some(clinicId => clinicIdsInCohort.includes(clinicId));
      });
    }

    // Filter by specific clinic if selected (based on current availability)
    if (selectedClinic) {
      filtered = filtered.filter((clinician) => {
        const currentClinicIds = getCurrentClinicIds(clinician);
        return currentClinicIds.includes(selectedClinic._id);
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((clinician) =>
        `${clinician.firstName} ${clinician.lastName}`.toLowerCase().includes(query) ||
        clinician.email.toLowerCase().includes(query) ||
        clinician.qualifications?.toLowerCase().includes(query) ||
        clinician.defaultLocation?.name?.toLowerCase().includes(query)
      );
    }

    // If search result is selected, show only that clinician
    if (selectedSearchResult) {
      filtered = filtered.filter(clinician => clinician._id === selectedSearchResult._id);
    }

    return filtered;
  }, [clinicians, selectedClinic, selectedPayrollCohort, searchQuery, selectedSearchResult, clinics]);

  // Filter by status
  const activeClinicians = useMemo(
    () => filteredClinicians.filter((clinician) => clinician.userStatus === "active"),
    [filteredClinicians]
  );

  const inactiveClinicians = useMemo(
    () => filteredClinicians.filter((clinician) => clinician.userStatus === "inactive"),
    [filteredClinicians]
  );

  useEffect(() => {
    logInfo("CliniciansPage rendered", {
      componentName: "CliniciansPage",
      cliniciansCount: clinicians.length,
    });
  }, [clinicians]);

  return (
    <div>
      {/* Search and Filter Section */}
      <div className="mb-6 bg-white p-4 rounded-sm shadow-sm ring-1 ring-gray-900/5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex-1 space-y-4 sm:space-y-0 sm:space-x-4 sm:flex">
            {/* Search Dropdown */}
            <div className="w-full sm:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Clinicians
              </label>
              <div className="relative">
                <SearchDropdown
                  displayFields={["firstName", "lastName", "email"]}
                  onSelection={handleSearchSelection}
                  prefix=":user:"
                  placeholder="Search by name or email..."
                  initialSelection={selectedSearchResult}
                />
                {selectedSearchResult && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Payroll Cohort Filter */}
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Cohort
              </label>
              <Listbox value={selectedPayrollCohort} onChange={handleCohortFilterChange}>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-pointer rounded-sm border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    <span className="block truncate">
                      {selectedPayrollCohort ? selectedPayrollCohort.name : "All Cohorts"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={React.Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                      <Listbox.Option
                        value={null}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? "bg-gray-50 text-gray-900" : "text-gray-700"
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                              All Cohorts
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                <CheckIcon className="h-4 w-4" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                      {payrollCohorts.map((cohort) => {
                        const cliniciansCount = clinicians.filter(clinician => {
                          const clinicsInCohort = clinics.filter(c => c.payrollCohort?._id === cohort._id);
                          const clinicIdsInCohort = clinicsInCohort.map(c => c._id);
                          const currentClinicIds = getCurrentClinicIds(clinician);
                          return currentClinicIds.some(clinicId => clinicIdsInCohort.includes(clinicId));
                        }).length;
                        
                        return (
                          <Listbox.Option
                            key={cohort._id}
                            value={cohort}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                active ? "bg-indigo-50 text-indigo-900" : "text-gray-700"
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                  {cohort.name} ({cliniciansCount})
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                    <CheckIcon className="h-4 w-4" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        );
                      })}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            {/* Clinic Filter */}
            <div className="flex-1 min-w-[320px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Clinic {selectedPayrollCohort && (
                  <span className="text-indigo-600">({selectedPayrollCohort.name} only)</span>
                )}
              </label>
              <Listbox value={selectedClinic} onChange={handleClinicFilterChange}>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-pointer rounded-sm border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    <span className="block truncate">
                      {selectedClinic ? selectedClinic.name : "All Clinics"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={React.Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none">
                      <Listbox.Option
                        value={null}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? "bg-gray-50 text-gray-900" : "text-gray-700"
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                              All Clinics
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                <CheckIcon className="h-4 w-4" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                      {clinics
                        .filter(clinic => !selectedPayrollCohort || clinic.payrollCohort?._id === selectedPayrollCohort._id)
                        .map((clinic) => {
                          const cliniciansCount = clinicians.filter(clinician => {
                            const currentClinicIds = getCurrentClinicIds(clinician);
                            return currentClinicIds.includes(clinic._id);
                          }).length;
                          
                          return (
                            <Listbox.Option
                              key={clinic._id}
                              value={clinic}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                  active ? "bg-indigo-50 text-indigo-900" : "text-gray-700"
                                }`
                              }
                            >
                              {({ selected }) => (
                                <>
                                  <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                    {clinic.name} ({cliniciansCount})
                                  </span>
                                  {selected && (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                      <CheckIcon className="h-4 w-4" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Listbox.Option>
                          );
                        })}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {(selectedClinic || selectedPayrollCohort || selectedSearchResult || searchQuery) && (
              <button
                onClick={handleClearFilters}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Clear Filters
              </button>
            )}
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Filter Status */}
        {(selectedClinic || selectedPayrollCohort || selectedSearchResult) && (
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
            <span>Active Filters:</span>
            {selectedPayrollCohort && (
              <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-purple-100 text-purple-800">
                Cohort: {selectedPayrollCohort.name}
                <button
                  onClick={() => setSelectedPayrollCohort(null)}
                  className="ml-1 hover:text-purple-600"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedClinic && (
              <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800">
                Clinic: {selectedClinic.name}
                <button
                  onClick={() => setSelectedClinic(null)}
                  className="ml-1 hover:text-blue-600"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
            {selectedSearchResult && (
              <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-green-100 text-green-800">
                Search: {selectedSearchResult.firstName} {selectedSearchResult.lastName}
                <button
                  onClick={handleClearSearch}
                  className="ml-1 hover:text-green-600"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      <Table
        columns={[
          { header: "First Name", accessor: "firstName", sortable: true },
          { header: "Last Name", accessor: "lastName", sortable: true },
          { header: "Email", accessor: "email" },
          { 
            header: "Current Clinics", 
            accessor: (row) => {
              const currentClinics = getCurrentClinics(row);
              if (currentClinics.length === 0) {
                return <span className="text-gray-500">Not available</span>;
              }
              
              if (currentClinics.length === 1) {
                return (
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={(e) => handleShowAvailability(row, e)}
                      className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                      title="View clinic availability details"
                    >
                      {currentClinics[0]}
                    </button>
                  </div>
                );
              }
              
              return (
                <div className="flex flex-wrap gap-1">
                  {currentClinics.slice(0, 2).map((clinic, index) => (
                    <button
                      key={index}
                      onClick={(e) => handleShowAvailability(row, e)}
                      className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                      title="View clinic availability details"
                    >
                      {clinic}
                    </button>
                  ))}
                  {currentClinics.length > 2 && (
                    <button
                      onClick={(e) => handleShowAvailability(row, e)}
                      className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors cursor-pointer"
                      title="View all clinic availability details"
                    >
                      +{currentClinics.length - 2} more
                    </button>
                  )}
                </div>
              );
            }
          },
          { header: "Qualifications", accessor: "qualifications" },
        ]}
        data={getSortedClinicians(activeClinicians)}
        onRowClick={(clinician) => handleClinicianClick(clinician)}
        sortConfig={sortConfig}
        onSortChange={handleSort}
        actions={[
          { 
            label: "Add Clinician", 
            onClick: handleAddClinician,
            variant: "primary"
          }
        ]}
        refreshAction={handleRefresh}
        title="Active Clinicians"
        description="A list of all active clinicians with HearSay."
      />
      <div className="py-6">
        <Table
          columns={[
            { header: "First Name", accessor: "firstName", sortable: true },
            { header: "Last Name", accessor: "lastName", sortable: true },
            { header: "Email", accessor: "email" },
            { 
              header: "Current Clinics", 
              accessor: (row) => {
                const currentClinics = getCurrentClinics(row);
                if (currentClinics.length === 0) {
                  return <span className="text-gray-500">Not available</span>;
                }
                
                if (currentClinics.length === 1) {
                  return (
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={(e) => handleShowAvailability(row, e)}
                        className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                        title="View clinic availability details"
                      >
                        {currentClinics[0]}
                      </button>
                    </div>
                  );
                }
                
                return (
                  <div className="flex flex-wrap gap-1">
                    {currentClinics.slice(0, 2).map((clinic, index) => (
                      <button
                        key={index}
                        onClick={(e) => handleShowAvailability(row, e)}
                        className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                        title="View clinic availability details"
                      >
                        {clinic}
                      </button>
                    ))}
                    {currentClinics.length > 2 && (
                      <button
                        onClick={(e) => handleShowAvailability(row, e)}
                        className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors cursor-pointer"
                        title="View all clinic availability details"
                      >
                        +{currentClinics.length - 2} more
                      </button>
                    )}
                  </div>
                );
              }
            },
            { header: "Qualifications", accessor: "qualifications" },
          ]}
          data={getSortedClinicians(inactiveClinicians)}
          onRowClick={(clinician) => handleClinicianClick(clinician)}
          sortConfig={sortConfig}
          onSortChange={handleSort}
          title="Inactive Clinicians"
          description="A list of all inactive clinicians with HearSay."
        />
      </div>

      {isQuickAddClinicianModalOpen && (
        <QuickAddClinicianModal onClose={handleCloseQuickAddClinicianModal} />
      )}
      {selectedClinician && (
        <>
          <ClinicianDetailsModal
            clinicianId={selectedClinician}
            onClose={() => {
              setSelectedClinician(null);
            }}
          />
        </>
      )}

      {/* Clinic Availability Modal */}
      <Transition show={availabilityModalOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setAvailabilityModalOpen(false)}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setAvailabilityModalOpen(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-semibold leading-6 text-gray-900 mb-4"
                      >
                        Clinic Availability - {selectedClinicianForAvailability?.firstName} {selectedClinicianForAvailability?.lastName}
                      </Dialog.Title>

                      {selectedClinicianForAvailability?.availability && selectedClinicianForAvailability.availability.length > 0 ? (
                        <div className="space-y-6">
                          {(() => {
                            const now = new Date();
                            const currentAvailability = [];
                            const previousAvailability = [];
                            const futureAvailability = [];
                            
                            selectedClinicianForAvailability.availability.forEach((avail, index) => {
                              const startDate = avail.startDate ? new Date(avail.startDate) : null;
                              const endDate = avail.endDate ? new Date(avail.endDate) : null;
                              const isCurrentlyActive = (!startDate || startDate <= now) && (!endDate || endDate >= now);
                              const isFuture = startDate && startDate > now;
                              const isPast = endDate && endDate < now;
                              
                              const availabilityWithMetadata = { ...avail, index, startDate, endDate, isCurrentlyActive };
                              
                              if (isCurrentlyActive) {
                                currentAvailability.push(availabilityWithMetadata);
                              } else if (isFuture) {
                                futureAvailability.push(availabilityWithMetadata);
                              } else if (isPast) {
                                previousAvailability.push(availabilityWithMetadata);
                              }
                            });

                            const AvailabilityCard = ({ avail, showStatus = false }) => {
                              const clinic = clinics.find(c => c._id === avail.clinic);
                              return (
                                <div 
                                  key={avail.index} 
                                  className={`border rounded-sm p-4 ${
                                    avail.isCurrentlyActive ? 'bg-green-50 border-green-200' : 
                                    avail.startDate > now ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <h5 className="text-lg font-medium flex items-center">
                                      <BuildingOffice2Icon className="h-5 w-5 text-gray-500 mr-2" />
                                      {clinic?.name || 'Unknown Clinic'}
                                      {showStatus && avail.isCurrentlyActive && (
                                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-green-100 text-green-800">
                                          Currently Active
                                        </span>
                                      )}
                                    </h5>
                                    <div className="text-sm text-gray-600">
                                      {avail.startDate ? avail.startDate.toLocaleDateString() : 'No start date'} - {avail.endDate ? avail.endDate.toLocaleDateString() : 'Ongoing'}
                                    </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-7 gap-2 text-sm">
                                    {Object.entries(avail.schedule || {}).map(([day, times]) => (
                                      <div key={day} className="text-center">
                                        <div className="font-medium text-gray-700 mb-1">{day.slice(0, 3)}</div>
                                        <div className="text-xs text-gray-600">
                                          {Array.isArray(times) && times.length > 0 ? (
                                            times.map((time, timeIndex) => (
                                              <div key={timeIndex}>
                                                {time.start && time.end ? `${time.start}-${time.end}` : 'Not available'}
                                              </div>
                                            ))
                                          ) : (
                                            <div>Not available</div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            };

                            return (
                              <>
                                {/* Current Availability */}
                                {currentAvailability.length > 0 && (
                                  <div>
                                    <h4 className="text-lg font-medium text-gray-800 mb-3 flex items-center">
                                      <span className="inline-flex items-center w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                                      Current Assignments ({currentAvailability.length})
                                    </h4>
                                    <div className="space-y-3">
                                      {currentAvailability.map(avail => (
                                        <AvailabilityCard key={avail.index} avail={avail} showStatus={true} />
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Future Availability */}
                                {futureAvailability.length > 0 && (
                                  <details className="group">
                                    <summary className="cursor-pointer text-lg font-medium text-gray-800 mb-3 flex items-center hover:text-gray-600">
                                      <span className="inline-flex items-center w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                                      Future Assignments ({futureAvailability.length})
                                      <ChevronUpDownIcon className="ml-2 h-5 w-5 transform group-open:rotate-180 transition-transform" />
                                    </summary>
                                    <div className="space-y-3 mt-3">
                                      {futureAvailability.map(avail => (
                                        <AvailabilityCard key={avail.index} avail={avail} />
                                      ))}
                                    </div>
                                  </details>
                                )}

                                {/* Previous Availability */}
                                {previousAvailability.length > 0 && (
                                  <details className="group">
                                    <summary className="cursor-pointer text-lg font-medium text-gray-800 mb-3 flex items-center hover:text-gray-600">
                                      <span className="inline-flex items-center w-3 h-3 bg-gray-500 rounded-full mr-2"></span>
                                      Previous Assignments ({previousAvailability.length})
                                      <ChevronUpDownIcon className="ml-2 h-5 w-5 transform group-open:rotate-180 transition-transform" />
                                    </summary>
                                    <div className="space-y-3 mt-3">
                                      {previousAvailability.map(avail => (
                                        <AvailabilityCard key={avail.index} avail={avail} />
                                      ))}
                                    </div>
                                  </details>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No availability data</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            This clinician does not have any clinic availability configured.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default CliniciansPage;
